import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { dashboardAPI, invoiceAPI, labelAPI } from '../../lib/api';
import { formatCurrency, formatNumber } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import TrackAnalyticsModal from '../../components/TrackAnalyticsModal';
import HealthCheckCard from '../../components/HealthCheckCard';
import { 
    Music, 
    Building2, 
    IndianRupee, 
    FileText, 
    TrendingUp,
    Upload,
    ArrowRight,
    Loader2,
    AlertCircle,
    CheckCircle,
    XCircle,
    Play,
    Trash2,
    DollarSign,
    Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const StatCard = ({ title, value, subtitle, icon: Icon, color = 'primary', link }) => (
    <Card className="bg-card/50 border-border/40 hover:border-border/60 transition-all duration-300" data-testid={`stat-${title.toLowerCase().replace(/\s+/g, '-')}`}>
        <CardContent className="p-6">
            <div className="flex items-start justify-between">
                <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">{title}</p>
                    <p className="text-3xl font-heading font-bold tracking-tight">{value}</p>
                    {subtitle && (
                        <p className="text-sm text-muted-foreground">{subtitle}</p>
                    )}
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    color === 'primary' ? 'bg-primary/20 text-primary' :
                    color === 'secondary' ? 'bg-secondary/20 text-secondary' :
                    color === 'accent' ? 'bg-accent/20 text-accent' :
                    'bg-muted text-muted-foreground'
                }`}>
                    <Icon className="w-6 h-6" />
                </div>
            </div>
            {link && (
                <Link to={link} className="mt-4 inline-flex items-center text-sm text-primary hover:underline">
                    View details <ArrowRight className="ml-1 w-4 h-4" />
                </Link>
            )}
        </CardContent>
    </Card>
);

const AdminDashboard = () => {
    const { user, isFounder } = useAuth();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedTrack, setSelectedTrack] = useState(null);
    const [trackModalOpen, setTrackModalOpen] = useState(false);
    const [viewMode, setViewMode] = useState('cash_flow'); // 'performance' or 'cash_flow'
    const [pendingApprovals, setPendingApprovals] = useState({
        pending_asset_deletions: [],
        pending_invoices: [],
        pending_revenue_changes: [],
        totals: { asset_deletions: 0, invoices: 0, revenue_changes: 0, total: 0 }
    });
    const token = localStorage.getItem('token');

    const fetchPendingApprovals = async () => {
        if (user?.role !== 'founder' && user?.role !== 'admin') return;
        
        try {
            const response = await fetch(`${API_URL}/api/dashboard/founder/pending-approvals`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setPendingApprovals(data);
            }
        } catch (error) {
            console.error('Failed to fetch pending approvals:', error);
        }
    };

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const monthType = viewMode === 'performance' ? 'transaction_month' : 'reporting_month';
                const response = await dashboardAPI.getAdmin(monthType);
                setStats(response.data);
            } catch (error) {
                console.error('Failed to fetch dashboard stats:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
        fetchPendingApprovals();
    }, [user, viewMode]); // Re-fetch when viewMode changes

    // Approval handlers - only for founder
    const handleInvoiceApproval = async (invoiceId, action) => {
        try {
            const response = await fetch(`${API_URL}/api/invoices/${invoiceId}/founder-approve?action=${action}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Operation failed');
            }
            toast.success(action === 'approve' ? 'Invoice approved' : 'Invoice rejected');
            fetchPendingApprovals();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleAssetApproval = async (assetId, action) => {
        try {
            const endpoint = action === 'approve' 
                ? `${API_URL}/api/assets/${assetId}/approve-delete`
                : `${API_URL}/api/assets/${assetId}/reject-delete`;
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Operation failed');
            }
            toast.success(action === 'approve' ? 'Asset deleted' : 'Delete request rejected');
            fetchPendingApprovals();
        } catch (error) {
            toast.error(error.message);
        }
    };

    const handleRevenueShareApproval = async (labelId, action) => {
        try {
            const response = await fetch(`${API_URL}/api/labels/${labelId}/approve-revenue-share?action=${action}`, {
                method: 'PUT',
                headers: { 
                    'Authorization': `Bearer ${token}`
                }
            });
            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Operation failed');
            }
            toast.success(`Revenue share change ${action}d`);
            fetchPendingApprovals();
        } catch (error) {
            toast.error(error.message);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-[50vh]">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    const topTrack = stats?.top_tracks?.[0];
    const totalDecisions = pendingApprovals?.totals?.total || 0;
    const invoiceApprovalValue = (pendingApprovals?.pending_invoices || []).reduce((sum, inv) => sum + Number(inv?.amount || 0), 0);
    const grossRevenue = stats?.gross_revenue_inr ?? stats?.total_revenue_inr ?? 0;
    const netRevenue = stats?.net_revenue_inr || 0;
    const labelPayout = stats?.label_payout_inr || 0;
    const outstandingAmount = stats?.outstanding_amount_inr || 0;
    const outstandingInvoiced = stats?.outstanding_invoiced_inr || 0;
    const outstandingUnbilled = stats?.outstanding_unbilled_inr || 0;
    const payoutRatio = grossRevenue > 0 ? Math.round((labelPayout / grossRevenue) * 100) : 0;
    const founderSignals = [
        {
            label: 'Decision status',
            value: totalDecisions > 0 ? `${totalDecisions} pending` : 'All clear',
            note: totalDecisions > 0
                ? `${formatCurrency(invoiceApprovalValue, 'INR')} invoice value needs founder action.`
                : 'No invoices, revenue-share changes or asset deletions need approval.'
        },
        {
            label: 'Payout health',
            value: `${payoutRatio}% payout ratio`,
            note: `${formatCurrency(labelPayout, 'INR')} payable to labels against gross revenue.`
        },
        {
            label: 'Catalog leader',
            value: topTrack?.title || 'No leader yet',
            note: topTrack
                ? `${formatCurrency(topTrack.revenue_inr || topTrack.revenue || 0, 'INR')} generated by the top track.`
                : 'Revenue leaders will appear after revenue ingestion.'
        },
        {
            label: 'Cash exposure',
            value: outstandingAmount > 0 ? formatCurrency(outstandingAmount, 'INR') : 'No exposure',
            note: outstandingAmount > 0
                ? `${formatCurrency(outstandingInvoiced, 'INR')} invoiced · ${formatCurrency(outstandingUnbilled, 'INR')} unbilled.`
                : 'No unpaid or unbilled payout exposure right now.'
        }
    ];

    return (
        <div className="space-y-8" data-testid="admin-dashboard">
            {/* Header with View Mode Toggle */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-3xl font-heading font-bold tracking-tight">
                            {user?.role === 'founder' ? 'Founder Command Center' : 'Dashboard'}
                        </h1>
                        <p className="text-muted-foreground">
                            {user?.role === 'founder'
                                ? 'Executive overview and decisions waiting for your approval'
                                : (viewMode === 'performance' ? 'Performance data based on transaction month' : 'Cash flow data based on reporting month')}
                        </p>
                    </div>
                    
                    {/* View Mode Toggle */}
                    <div className="flex items-center gap-2 p-1 bg-muted/30 rounded-lg">
                        <button
                            onClick={() => setViewMode('performance')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                viewMode === 'performance'
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            Performance
                        </button>
                        <button
                            onClick={() => setViewMode('cash_flow')}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                                viewMode === 'cash_flow'
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            }`}
                        >
                            Cash Flow
                        </button>
                    </div>
                </div>
                
                {/* Quick Actions */}
                {user?.role === 'admin' && (
                    <div className="flex gap-3">
                        <Button asChild variant="outline" className="border-border/50">
                            <Link to="/admin/content" data-testid="upload-content-btn">
                                <Upload className="w-4 h-4 mr-2" />
                                Upload Content
                            </Link>
                        </Button>
                        <Button asChild className="bg-primary hover:bg-primary/90 glow-primary">
                            <Link to="/admin/revenue" data-testid="upload-revenue-btn">
                                <TrendingUp className="w-4 h-4 mr-2" />
                                Upload Revenue
                            </Link>
                        </Button>
                    </div>
                )}
            </div>

            {/* ELARA Health Check (Admin only) — prominent premium card */}
            {user?.role === 'admin' && <HealthCheckCard />}


            {/* Founder Command Center Hero */}
            {user?.role === 'founder' && (
                <Card className="bg-gradient-to-br from-primary/15 via-card to-secondary/10 border-primary/20 shadow-sm">
                    <CardContent className="p-6">
                        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                            <div className="space-y-3">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                                    <AlertCircle className="w-4 h-4" />
                                    Founder Action Center
                                </div>
                                <div>
                                    <h2 className="text-2xl font-heading font-bold">
                                        {pendingApprovals?.totals?.total > 0
                                            ? `${pendingApprovals.totals.total} decision${pendingApprovals.totals.total === 1 ? '' : 's'} need your attention`
                                            : 'All caught up. No approvals pending.'}
                                    </h2>
                                    <p className="text-muted-foreground mt-1">
                                        Review invoice approvals, revenue share changes and asset deletion requests from one place.
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-3 min-w-full lg:min-w-[420px]">
                                <div className="rounded-xl bg-background/60 border border-border/50 p-4">
                                    <p className="text-xs text-muted-foreground">Invoices</p>
                                    <p className="text-2xl font-heading font-bold text-yellow-500">
                                        {pendingApprovals?.totals?.invoices || 0}
                                    </p>
                                    <p className="text-xs text-muted-foreground">Founder approval</p>
                                </div>
                                <div className="rounded-xl bg-background/60 border border-border/50 p-4">
                                    <p className="text-xs text-muted-foreground">Revenue Changes</p>
                                    <p className="text-2xl font-heading font-bold text-secondary">
                                        {pendingApprovals?.totals?.revenue_changes || 0}
                                    </p>
                                    <p className="text-xs text-muted-foreground">Share requests</p>
                                </div>
                                <div className="rounded-xl bg-background/60 border border-border/50 p-4">
                                    <p className="text-xs text-muted-foreground">Asset Deletions</p>
                                    <p className="text-2xl font-heading font-bold text-destructive">
                                        {pendingApprovals?.totals?.asset_deletions || 0}
                                    </p>
                                    <p className="text-xs text-muted-foreground">Delete requests</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-wrap gap-3 mt-6">
                            <Button asChild className="bg-primary hover:bg-primary/90">
                                <Link to="/admin/invoices">
                                    Review Invoices
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Link>
                            </Button>
                            <Button asChild variant="outline" className="border-border/50">
                                <Link to="/admin/founder-analytics">
                                    Open Founder Analytics
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Link>
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Founder Executive Metrics */}
            {user?.role === 'founder' && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    <StatCard
                        title="Gross Revenue"
                        value={formatCurrency(stats?.gross_revenue_inr ?? stats?.total_revenue_inr ?? 0, 'INR')}
                        subtitle="Total revenue across catalog"
                        icon={IndianRupee}
                        color="secondary"
                        link="/admin/founder-analytics"
                    />
                    <StatCard
                        title="Net Revenue"
                        value={formatCurrency(stats?.net_revenue_inr || 0, 'INR')}
                        subtitle={`After ${formatCurrency(stats?.label_payout_inr || 0, 'INR')} label share`}
                        icon={IndianRupee}
                        color="primary"
                        link="/admin/founder-analytics"
                    />
                    <StatCard
                        title="Label Payout"
                        value={formatCurrency(stats?.label_payout_inr || 0, 'INR')}
                        subtitle="Payable share to labels"
                        icon={DollarSign}
                        color="secondary"
                        link="/admin/invoices"
                    />
                    <StatCard
                        title="Outstanding Amount"
                        value={formatCurrency(stats?.outstanding_amount_inr || 0, 'INR')}
                        subtitle={`${formatCurrency(stats?.outstanding_invoiced_inr || 0, 'INR')} invoiced · ${formatCurrency(stats?.outstanding_unbilled_inr || 0, 'INR')} unbilled`}
                        icon={AlertCircle}
                        color="accent"
                        link="/admin/invoices"
                    />
                    <StatCard
                        title="Founder Actions"
                        value={pendingApprovals?.totals?.invoices || 0}
                        subtitle="Pending founder decisions"
                        icon={FileText}
                        color="accent"
                        link="/admin/invoices"
                    />
                </div>
            )}



            {/* Founder Intelligence */}
            {user?.role === 'founder' && (
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                    <Card className="xl:col-span-2 bg-card/50 border-border/40 overflow-hidden">
                        <CardHeader>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <Zap className="w-5 h-5 text-secondary" />
                                ELARA Intelligence
                            </CardTitle>
                            <CardDescription>Executive signals that explain the dashboard without repeating every KPI</CardDescription>
                        </CardHeader>
                        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {founderSignals.map((item, index) => (
                                <div key={index} className="rounded-xl bg-muted/25 border border-border/40 p-4">
                                    <p className="text-xs uppercase tracking-wide text-muted-foreground">{item.label}</p>
                                    <p className="mt-2 text-lg font-semibold leading-snug">{item.value}</p>
                                    <p className="mt-1 text-sm text-muted-foreground leading-snug">{item.note}</p>
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-secondary/10 via-card to-primary/10 border-secondary/20">
                        <CardHeader>
                            <CardTitle className="font-heading">Cash & Payout Snapshot</CardTitle>
                            <CardDescription>Settlement exposure and payout mix</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Outstanding</span>
                                <span className="font-mono font-bold text-accent">{formatCurrency(outstandingAmount, 'INR')}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Invoiced</span>
                                <span className="font-mono">{formatCurrency(outstandingInvoiced, 'INR')}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Unbilled</span>
                                <span className="font-mono">{formatCurrency(outstandingUnbilled, 'INR')}</span>
                            </div>
                            <div className="pt-3 border-t border-border/40">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">Label payout ratio</span>
                                    <span className="font-semibold">{payoutRatio}%</span>
                                </div>
                                <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden">
                                    <div className="h-2 rounded-full bg-secondary" style={{ width: `${Math.min(payoutRatio, 100)}%` }} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Stats Grid */}
            {user?.role !== 'founder' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                <StatCard
                    title="Total Songs"
                    value={formatNumber(stats?.total_songs || 0)}
                    subtitle={`${stats?.recent_uploads || 0} added this week`}
                    icon={Music}
                    color="primary"
                    link="/admin/content"
                />
                <StatCard
                    title="Record Labels"
                    value={formatNumber(stats?.total_labels || 0)}
                    subtitle="Active partners"
                    icon={Building2}
                    color="secondary"
                    link="/admin/labels"
                />
                <StatCard
                    title="Gross Revenue"
                    value={formatCurrency(stats?.gross_revenue_inr ?? stats?.total_revenue_inr ?? 0, 'INR')}
                    subtitle="Across all labels"
                    icon={IndianRupee}
                    color="secondary"
                    link="/admin/revenue"
                    data-testid="stat-gross-revenue"
                />
                <StatCard
                    title="Net Revenue"
                    value={formatCurrency(stats?.net_revenue_inr || 0, 'INR')}
                    subtitle={`After ${formatCurrency(stats?.label_payout_inr || 0, 'INR')} label share`}
                    icon={IndianRupee}
                    color="primary"
                    link="/admin/revenue"
                    data-testid="stat-net-revenue"
                />
                <StatCard
                    title="Outstanding Amount"
                    value={formatCurrency(stats?.outstanding_amount_inr || 0, 'INR')}
                    subtitle={`${formatCurrency(stats?.outstanding_invoiced_inr || 0, 'INR')} invoiced · ${formatCurrency(stats?.outstanding_unbilled_inr || 0, 'INR')} unbilled`}
                    icon={AlertCircle}
                    color="accent"
                    link="/admin/invoices"
                    data-testid="stat-outstanding-amount"
                />
                <StatCard
                    title="Total Streams"
                    value={formatNumber(stats?.total_streams || 0)}
                    subtitle="All platforms"
                    icon={TrendingUp}
                    color="primary"
                    link="/admin/revenue"
                />
                <StatCard
                    title="Pending Invoices"
                    value={stats?.pending_invoices || 0}
                    subtitle="Awaiting approval"
                    icon={FileText}
                    color="accent"
                    link="/admin/invoices"
                />
            </div>
            )}

            {/* Pending Approvals Section - Founder Only */}
            {user?.role === 'founder' && pendingApprovals.totals.total > 0 && (
                <Card className="bg-card/50 border-border/40 border-l-4 border-l-yellow-500" data-testid="pending-approvals-alert">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-yellow-500" />
                            Founder Action Items
                            <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30 ml-2">
                                {pendingApprovals.totals.total}
                            </Badge>
                        </CardTitle>
                        <CardDescription>Decisions waiting for your approval</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Invoice Approvals */}
                        {pendingApprovals.pending_invoices.length > 0 && (
                            <div className="space-y-3">
                                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    Invoice Approvals ({pendingApprovals.pending_invoices.length})
                                </h4>
                                {pendingApprovals.pending_invoices.map((invoice) => (
                                    <div key={invoice.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
                                        <div className="flex items-center gap-3">
                                            <FileText className="w-5 h-5 text-green-500" />
                                            <div>
                                                <div className="font-medium">{invoice.invoice_number}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {invoice.label_name} • {invoice.month}
                                                </div>
                                                <div className="text-sm mt-1 font-semibold text-green-400">
                                                    {invoice.currency === 'INR' ? '₹' : '$'}{invoice.amount?.toLocaleString()}
                                                </div>
                                                {invoice.finance_notes && (
                                                    <div className="text-xs text-muted-foreground mt-1">
                                                        <span className="text-blue-400">Finance:</span> {invoice.finance_notes}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleInvoiceApproval(invoice.id, 'reject')}
                                                className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                                            >
                                                <XCircle className="w-4 h-4 mr-1" />
                                                Reject
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handleInvoiceApproval(invoice.id, 'approve')}
                                                className="bg-green-600 hover:bg-green-700"
                                            >
                                                <CheckCircle className="w-4 h-4 mr-1" />
                                                Approve
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Asset Deletion Requests */}
                        {pendingApprovals.pending_asset_deletions.length > 0 && (
                            <div className="space-y-3">
                                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                                    <Trash2 className="w-4 h-4" />
                                    Asset Deletion Requests ({pendingApprovals.pending_asset_deletions.length})
                                </h4>
                                {pendingApprovals.pending_asset_deletions.map((asset) => (
                                    <div key={asset.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
                                        <div className="flex items-center gap-3">
                                            <FileText className="w-5 h-5 text-primary" />
                                            <div>
                                                <div className="font-medium">{asset.filename}</div>
                                                <div className="text-xs text-muted-foreground">
                                                    {asset.file_type === 'revenue' ? 'Revenue Sheet' : 'Content Upload'} • 
                                                    {asset.records_processed?.toLocaleString() || 0} records
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-1">
                                                    <span className="text-yellow-400">By:</span> {asset.delete_requested_by}
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    <span className="text-yellow-400">Reason:</span> {asset.delete_request_reason}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleAssetApproval(asset.id, 'reject')}
                                                className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                                            >
                                                <XCircle className="w-4 h-4 mr-1" />
                                                Reject
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handleAssetApproval(asset.id, 'approve')}
                                                className="bg-green-600 hover:bg-green-700"
                                            >
                                                <CheckCircle className="w-4 h-4 mr-1" />
                                                Delete
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Revenue Share Changes */}
                        {pendingApprovals.pending_revenue_changes.length > 0 && (
                            <div className="space-y-3">
                                <h4 className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                                    <DollarSign className="w-4 h-4" />
                                    Revenue Share Changes ({pendingApprovals.pending_revenue_changes.length})
                                </h4>
                                {pendingApprovals.pending_revenue_changes.map((label) => (
                                    <div key={label.id} className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border/50">
                                        <div className="flex items-center gap-3">
                                            <Building2 className="w-5 h-5 text-primary" />
                                            <div>
                                                <div className="font-medium">{label.name}</div>
                                                <div className="text-xs text-muted-foreground">Code: {label.code}</div>
                                                <div className="text-sm mt-1">
                                                    <span className="text-muted-foreground">Current:</span> {label.current_share}% 
                                                    <span className="mx-2">→</span>
                                                    <span className="text-yellow-400 font-medium">New: {label.requested_share}%</span>
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    By: {label.requested_by}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => handleRevenueShareApproval(label.id, 'reject')}
                                                className="text-red-500 border-red-500/30 hover:bg-red-500/10"
                                            >
                                                <XCircle className="w-4 h-4 mr-1" />
                                                Reject
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => handleRevenueShareApproval(label.id, 'approve')}
                                                className="bg-green-600 hover:bg-green-700"
                                            >
                                                <CheckCircle className="w-4 h-4 mr-1" />
                                                Approve
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            )}

            {/* Top Performing Tracks */}
            {user?.role !== 'founder' && stats?.top_tracks && stats.top_tracks.length > 0 && (
                <Card className="bg-card/50 border-border/40">
                    <CardHeader>
                        <CardTitle className="font-heading flex items-center gap-2">
                            <Music className="w-5 h-5 text-secondary" />
                            Top Performing Tracks
                        </CardTitle>
                        <CardDescription>Highest earning songs across all labels</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="space-y-3">
                            {stats.top_tracks.map((track, index) => (
                                <div 
                                    key={index} 
                                    className="flex items-center gap-4 p-3 rounded-xl bg-muted/20 hover:bg-muted/30 transition-colors group cursor-pointer"
                                    onClick={() => {
                                        setSelectedTrack(track.title);
                                        setTrackModalOpen(true);
                                    }}
                                >
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold shrink-0">
                                        {index + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium truncate group-hover:text-primary transition-colors">
                                            {track.title}
                                        </p>
                                        <p className="text-sm text-muted-foreground truncate">
                                            {track.artist} • {formatNumber(track.streams)} streams
                                        </p>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <p className="font-mono font-bold text-secondary">
                                            {formatCurrency(track.revenue_inr, 'INR')}
                                        </p>
                                        <p className="text-xs text-muted-foreground">revenue</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Founder Executive Quick View */}
            {user?.role === 'founder' && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {stats?.top_tracks && stats.top_tracks.length > 0 && (
                        <Card className="bg-card/50 border-border/40">
                            <CardHeader>
                                <CardTitle className="font-heading flex items-center gap-2">
                                    <TrendingUp className="w-5 h-5 text-secondary" />
                                    Revenue Leaders
                                </CardTitle>
                                <CardDescription>Top tracks driving founder-level revenue</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3">
                                    {stats.top_tracks.slice(0, 5).map((track, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-4 p-3 rounded-xl bg-muted/20 hover:bg-muted/30 transition-colors group cursor-pointer"
                                            onClick={() => {
                                                setSelectedTrack(track.title);
                                                setTrackModalOpen(true);
                                            }}
                                        >
                                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-bold shrink-0">
                                                {index + 1}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium truncate group-hover:text-primary transition-colors">{track.title}</p>
                                                <p className="text-sm text-muted-foreground truncate">{track.artist} · {formatNumber(track.streams)} streams</p>
                                            </div>
                                            <div className="text-right shrink-0">
                                                <p className="font-mono font-bold text-secondary">{formatCurrency(track.revenue_inr || track.revenue || 0, 'INR')}</p>
                                                <p className="text-xs text-muted-foreground">revenue</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    <Card className="bg-card/50 border-border/40">
                        <CardHeader>
                            <CardTitle className="font-heading flex items-center gap-2">
                                <AlertCircle className="w-5 h-5 text-accent" />
                                Founder Risk Radar
                            </CardTitle>
                            <CardDescription>Simple operating signals</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/20">
                                <div>
                                    <p className="font-medium">Approval load</p>
                                    <p className="text-sm text-muted-foreground">Items waiting for founder decision</p>
                                </div>
                                <Badge className={totalDecisions > 0 ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' : 'bg-secondary/20 text-secondary border-secondary/30'}>
                                    {totalDecisions > 0 ? `${totalDecisions} pending` : 'Clear'}
                                </Badge>
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/20">
                                <div>
                                    <p className="font-medium">Cash exposure</p>
                                    <p className="text-sm text-muted-foreground">Outstanding payable amount</p>
                                </div>
                                <Badge variant="outline">{formatCurrency(outstandingAmount, 'INR')}</Badge>
                            </div>
                            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/20">
                                <div>
                                    <p className="font-medium">Top track concentration</p>
                                    <p className="text-sm text-muted-foreground">Highest-earning catalog dependency</p>
                                </div>
                                <Badge variant="outline">Monitor</Badge>
                            </div>
                            <div className="pt-3">
                                <Button asChild variant="outline" className="w-full border-border/50">
                                    <Link to="/admin/founder-analytics">
                                        Deep Dive Analytics
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </Link>
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Quick Actions */}
            <Card className="bg-card/50 border-border/40">
                <CardHeader>
                    <CardTitle className="font-heading">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {user?.role === 'admin' && (
                        <>
                            <Button asChild variant="outline" className="h-auto py-4 flex-col gap-2 border-border/50 hover:border-primary hover:bg-primary/20 hover:text-primary transition-all">
                                <Link to="/admin/content">
                                    <Music className="w-5 h-5 text-primary" />
                                    <span>Manage Content</span>
                                </Link>
                            </Button>
                            <Button asChild variant="outline" className="h-auto py-4 flex-col gap-2 border-border/50 hover:border-secondary hover:bg-secondary/20 hover:text-secondary transition-all">
                                <Link to="/admin/labels">
                                    <Building2 className="w-5 h-5 text-secondary" />
                                    <span>Add New Label</span>
                                </Link>
                            </Button>
                            <Button asChild variant="outline" className="h-auto py-4 flex-col gap-2 border-border/50 hover:border-accent hover:bg-accent/20 hover:text-accent transition-all">
                                <Link to="/admin/revenue">
                                    <TrendingUp className="w-5 h-5 text-accent" />
                                    <span>Upload Revenue</span>
                                </Link>
                            </Button>
                        </>
                    )}
                    <Button asChild variant="outline" className="h-auto py-4 flex-col gap-2 border-border/50 hover:border-primary hover:bg-primary/20 hover:text-primary transition-all">
                        <Link to="/admin/invoices">
                            <FileText className="w-5 h-5 text-primary" />
                            <span>Review Invoices</span>
                        </Link>
                    </Button>
                </CardContent>
            </Card>
            
            {/* Track Analytics Modal */}
            <TrackAnalyticsModal 
                open={trackModalOpen}
                onOpenChange={setTrackModalOpen}
                trackName={selectedTrack}
            />
        </div>
    );
};

export default AdminDashboard;
